import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { Text, View, Platform, Pressable, ScrollView, useWindowDimensions, StyleSheet } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import DefaultScreen from '@components/Global/DefaultScreenView';
import parseByDelimiter from '@utils/parseByDelimiter';
import { ToolbarContext } from '@hooks/toolbarContext';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SET_REPORT } from '@reduxLocal/actions/types';
import { debounce } from 'lodash';
import useGlobalUser from '@hooks/getGlobalUser';
import RangePicker from '@components/Global/DateRangePicker/RangePicker';
import moment from 'moment';
import { LinearGradient } from 'expo-linear-gradient';
import { FontAwesome5, MaterialIcons, Ionicons } from '@expo/vector-icons';
import ErrorBoundary from '@utils/ErrorBoundary';
import UserStatsTrends from './components/UserStatsTrends';
import AdvancedAnalyticsDashboard from './components/AdvancedAnalyticsDashboard';
import AnalyticsDemoData from './components/AnalyticsDemoData';
import ComprehensiveAnalytics from './components/ComprehensiveAnalytics';
import UsageInsights from './components/UsageInsights';
import CRMAnalytics from './components/CRMAnalytics';
import ProductAnalytics from './components/ProductAnalytics';
import '@css/lib/index.css';

// ApexCharts for web
let ReactApexChart: any = null;
if (Platform.OS === 'web') {
  try {
    ReactApexChart = require('react-apexcharts').default;
  } catch (error) {
    console.warn('ApexCharts not available for web');
  }
}

const ReportsScreen = ({ navigation: routeNavigation }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { reportRef, setReportRef } = useContext(ToolbarContext);
  const reportSelector = useSelector((state: DefaultRootState) => state.report);
  const contextOpen = useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  const theme = themeSelector();
  const ref = React.useRef(null);
  const [dateSelected, setDateSelected] = useState({
    startDate: moment().subtract(365, 'days').toDate(),
    endDate: moment().toDate()
  });
  const { width, height } = useWindowDimensions();
  const [filterType, setFilterType] = useState(false);
  const [reportData, setReportData] = useState<{ _id: string }>({ _id: "" });
  const [currentView, setCurrentView] = useState<'overview' | 'analytics' | 'demo' | 'comprehensive' | 'usage' | 'crm' | 'products'>('overview');
  const [contributionData, setContributionData] = useState();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('')
  const userId = useGlobalUser();
  const [extendedReports, setExtendedReports] = useState([]);

  console.log('width', width);

  // Responsive breakpoints
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  // Theme-aware styles
  const cardStyle = useMemo(() => getModernCardStyle(theme), [theme]);
  const largeCardStyle = useMemo(() => getModernCardStyle(theme, 'large'), [theme]);
  const textColor = useMemo(() => getModernTextColor(theme), [theme]);
  const secondaryTextColor = useMemo(() => getModernTextColor(theme, 'secondary'), [theme]);
  const mutedTextColor = useMemo(() => getModernTextColor(theme, 'muted'), [theme]);

  // Main reports data query
  const { status, data: mainChartsData, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['reportData', dateSelected.startDate, dateSelected.endDate],
    queryFn: async () => {
      const data = await apis.getMainCharts(dateSelected.startDate, dateSelected.endDate);
      return data;
    },
    enabled: !!dateSelected.startDate && !!dateSelected.endDate
  });

  // User statistics query
  const { data: userStatsData, isLoading: userStatsLoading } = useQuery({
    queryKey: ['userStats'],
    queryFn: () => apis.getUserStats(),
    staleTime: 5 * 60 * 1000,
  });

  // Sales statistics query
  const { data: salesStatsData, isLoading: salesStatsLoading } = useQuery({
    queryKey: ['salesStats'],
    queryFn: () => apis.getSalesStats(),
    staleTime: 5 * 60 * 1000,
  });

  // CRM count query
  const { data: crmCountData, isLoading: crmCountLoading } = useQuery({
    queryKey: ['crmCount'],
    queryFn: () => apis.getCrmCount(),
    staleTime: 5 * 60 * 1000,
  });

  // User statistics data with date range
  const { data: userStatsDateData, isLoading: userStatsDateLoading } = useQuery({
    queryKey: ['userStatsData', 'overview', dateSelected.startDate, dateSelected.endDate, filterType],
    queryFn: () => apis.getUserStatsData('overview', dateSelected.startDate, dateSelected.endDate, filterType),
    enabled: !!dateSelected.startDate && !!dateSelected.endDate,
    staleTime: 2 * 60 * 1000,
  });

  // Combine all data for DomReports
  const combinedReportsData = {
    mainCharts: mainChartsData,
    userStats: userStatsData,
    salesStats: salesStatsData,
    crmCount: crmCountData,
    userStatsDate: userStatsDateData,
    dateRange: dateSelected,
    loading: {
      main: Boolean(isLoading),
      userStats: Boolean(userStatsLoading),
      salesStats: Boolean(salesStatsLoading),
      crmCount: Boolean(crmCountLoading),
      userStatsDate: Boolean(userStatsDateLoading)
    }
  };

  // Helper to safely get nested values from API responses
  const getStatValue = (data: any, path: string, defaultValue: any = '0') => {
    try {
      if (!data) return defaultValue;
      const value = path.split('.').reduce((obj, key) => obj?.[key], data.data || data);
      if (value === null || value === undefined) return defaultValue;
      if (typeof value === 'object' && value !== null) return defaultValue;
      if (typeof value === 'number') return value.toLocaleString();
      if (typeof value !== 'string') return String(value);
      return value || defaultValue;
    } catch (error) {
      console.error(`getStatValue error for path ${path}:`, error);
      return defaultValue;
    }
  };

  // Quick stats component with Hero styling
  const QuickStatsCard = ({ title, value, icon, color = 'blue' as 'green' | 'blue' | 'red' | 'amber' | 'grey', growth = null, isLoading = false }) => {
    const safeValue = typeof value === 'object' ? '0' : String(value || '0');
    const safeGrowth = growth && typeof growth === 'string' ? growth : null;
    
    const colorMap = {
      blue: { bg: 'rgba(59, 130, 246, 0.1)', icon: '#3b82f6' },
      green: { bg: 'rgba(34, 197, 94, 0.1)', icon: '#22c55e' },
      amber: { bg: 'rgba(245, 158, 11, 0.1)', icon: '#f59e0b' },
      grey: { bg: 'rgba(139, 92, 246, 0.1)', icon: '#8b5cf6' },
    };

    return (
      <Animatable.View
        animation="fadeInUp"
        duration={600}
        style={[
          heroStyles.glassCard,
          {
            flex: isMobile ? 1 : isTablet ? 0.48 : 0.23,
            minWidth: isMobile ? '100%' : isTablet ? '48%' : '23%',
            marginBottom: 16,
            shadowOpacity: 0,
            marginHorizontal: isMobile ? 0 : 8,
          }
        ]}
      >
        <LinearGradient
          colors={theme
            ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']
            : ['rgba(200, 162, 200, 0.25)', 'rgba(135, 206, 235, 0.25)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={heroStyles.gradientBackground}
        />
        <View style={heroStyles.contentContainer}>
          <View style={[heroStyles.iconContainer, { backgroundColor: colorMap[color]?.bg || colorMap.blue.bg }]}>
            {icon}
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={[heroStyles.statLabel, { color: mutedTextColor }]}>{title}</Text>
            <Text style={[heroStyles.statValue, { color: textColor }]}>
              {isLoading ? '...' : safeValue}
            </Text>
            {safeGrowth && (
              <Text style={[heroStyles.growthText, { color: safeGrowth.startsWith('+') ? '#22c55e' : '#ef4444' }]}>
                {safeGrowth}
              </Text>
            )}
          </View>
        </View>
      </Animatable.View>
    );
  };

  // Section header with Hero styling
  const SectionHeader = ({ title, subtitle, action = null }) => (
    <Animatable.View
      animation="fadeInDown"
      duration={500}
      style={[heroStyles.sectionHeader, { marginHorizontal: isMobile ? 16 : 20 }]}
    >
      <View style={{ flex: 1 }}>
        <Text style={[heroStyles.sectionTitle, { color: textColor }]}>{title}</Text>
        {subtitle && (
          <Text style={[heroStyles.sectionSubtitle, { color: secondaryTextColor }]}>{subtitle}</Text>
        )}
      </View>
      {action}
    </Animatable.View>
  );

  // View selector with Hero styling
  const ViewSelector = () => (
    <Animatable.View
      animation="slideInDown"
      duration={600}
      style={[heroStyles.glassCard, { marginHorizontal: isMobile ? 16 : 20, marginBottom: 16 }]}
    >
      <LinearGradient
        colors={theme
          ? ['rgba(255, 255, 255, 0.95)', 'rgba(255, 255, 255, 0.85)']
          : ['rgba(30, 30, 30, 0.95)', 'rgba(30, 30, 30, 0.85)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={heroStyles.gradientBackground}
      />
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ flexDirection: 'row' }}
        contentContainerStyle={{ paddingHorizontal: 8, paddingVertical: 8 }}
      >
        {[
          { key: 'overview', label: 'Overview', icon: 'dashboard' },
          { key: 'comprehensive', label: 'Data Insights', icon: 'insights' },
          { key: 'usage', label: 'Usage Analytics', icon: 'trending-up' },
          { key: 'crm', label: 'CRM Analytics', icon: 'people' },
          { key: 'products', label: 'Products', icon: 'inventory' },
          { key: 'analytics', label: 'Advanced Charts', icon: 'show-chart' },
          { key: 'demo', label: 'Demo Data', icon: 'preview' },
          { key: 'custom', label: 'My Dashboards', icon: 'view-dashboard', navigate: true }
        ].map((view, index) => (
          <Animatable.View
            key={view.key}
            animation="fadeInRight"
            delay={index * 50}
            duration={400}
          >
            <Pressable
              onPress={() => {
                if (view.navigate) {
                  (navigation as any).navigate('CustomDashboards');
                } else {
                  setCurrentView(view.key as any);
                }
              }}
              style={[
                heroStyles.viewButton,
                currentView === view.key ? heroStyles.viewButtonActive : heroStyles.viewButtonInactive,
                {
                  backgroundColor: currentView === view.key
                    ? '#3b82f6'
                    : theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                }
              ]}
            >
              <MaterialIcons
                name={view.icon as any}
                size={18}
                color={currentView === view.key ? '#ffffff' : mutedTextColor}
              />
              <Text style={[
                heroStyles.viewButtonText,
                {
                  color: currentView === view.key ? '#ffffff' : mutedTextColor,
                }
              ]}>
                {view.label}
              </Text>
            </Pressable>
          </Animatable.View>
        ))}
      </ScrollView>
    </Animatable.View>
  );

  // Date range selector with Hero styling
  const DateRangeSelector = () => (
    <Animatable.View
      animation="fadeInUp"
      duration={500}
      style={[heroStyles.glassCard, { marginHorizontal: isMobile ? 16 : 20, marginBottom: 16 }]}
    >
      <View style={[heroStyles.contentContainer, { backgroundColor: 'transparent' }]}>
        <Text style={[heroStyles.fieldLabel, { color: mutedTextColor }]}>Date Range</Text>
        <RangePicker
          startDate={dateSelected.startDate}
          endDate={dateSelected.endDate}
        />
      </View>
    </Animatable.View>
  );

  // Update queries when date changes
  useEffect(() => {
    if (dateSelected.startDate && dateSelected.endDate) {
      queryClient.invalidateQueries({ queryKey: ['reportData'] });
      queryClient.invalidateQueries({ queryKey: ['userStatsData'] });
      refetch();
    }
  }, [dateSelected, refetch, queryClient]);

  // Refresh all reports data
  const refreshAllData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['reportData'] }),
      queryClient.invalidateQueries({ queryKey: ['userStats'] }),
      queryClient.invalidateQueries({ queryKey: ['salesStats'] }),
      queryClient.invalidateQueries({ queryKey: ['crmCount'] }),
      queryClient.invalidateQueries({ queryKey: ['userStatsData'] }),
    ]);
  }, [queryClient]);

  const _reportCallback = useCallback((report) => {
    dispatch({ type: SET_REPORT, payload: report._id })
    setReportData(report);
  }, [reportData]);

  useEffect(() => {
    if (reportData?._id) {
      getContributionData();
      _getExtendedStats();
    }
  }, [reportData])

  const getContributionData = async () => {
    setLoading(true);
    await apis.getContributionChart(reportData?._id).then(res => {
      setContributionData(res);
    }).finally(() => {
      setLoading(false);
    })
  }

  const _getExtendedStats = async () => {
    await apis.getExtendedData(reportData?._id, userId).then(res => {
      setExtendedReports(res);
    })
  }

  const _handleQuery = useCallback(async (query) => {
    debounce(async () => {
      await apis.getAIReportQuery(reportData?._id, query).then(res => {
        console.log(res, 'query server');
      })
    }, 1000)
  }, []);

  return (
    <View style={heroStyles.container}>
      {/* Hero-style gradient background */}
      <DefaultScreen
        screenTitle="Analytics Dashboard"
        style={[styles.modernMainContent]}
        containerStyle={{
          flex: 1,
          paddingTop: Platform.OS === 'web' ? 40 : 80,
          backgroundColor: 'transparent',
        }}
        searchBar={{
          placeHolder: 'Ask AI about your reports...',
          callback: _handleQuery,
          extraComponents: (
            <View style={[styles.centerEverything, { minWidth: isMobile ? 200 : 280, marginTop: -8 }]}>
              <Pressable 
                style={[heroStyles.refreshButton, { backgroundColor: '#3b82f6' }]}
                onPress={refreshAllData}
              >
                <Ionicons name="refresh" size={16} color="white" style={{ marginRight: 8 }} />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>Refresh</Text>
              </Pressable>
            </View>
          )
        }}
      >
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
        >
          {/* View Selector */}
          <ViewSelector />

          {currentView === 'overview' ? (
            <>
              {/* Date Range Selector */}
              <DateRangeSelector />

              {/* User Stats Trends */}
              <UserStatsTrends
                startDate={dateSelected.startDate}
                endDate={dateSelected.endDate}
              />

              {/* Quick Stats Overview */}
              <View style={getModernCardStyle(theme)}>
              <SectionHeader
                title="Quick Overview"
                subtitle="Key metrics at a glance"
              />

              <View style={[
                heroStyles.statsGrid,
                {
                  flexDirection: isMobile ? 'column' : 'row',
                  flexWrap: isMobile ? 'nowrap' : 'wrap',
                  paddingHorizontal: isMobile ? 16 : 20,
                }
              ]}>
                <QuickStatsCard
                  title="Total Users"
                  value={getStatValue(combinedReportsData.userStats, 'totalUsers', '0')}
                  icon={<FontAwesome5 name="users" size={20} color="#3b82f6" />}
                  color="blue"
                  growth={getStatValue(combinedReportsData.userStatsDate, 'userGrowth', null)}
                  isLoading={combinedReportsData.loading?.userStats}
                />

                <QuickStatsCard
                  title="Revenue"
                  value={`$${getStatValue(combinedReportsData.salesStats, 'totalIncome', '0')}`}
                  icon={<FontAwesome5 name="dollar-sign" size={20} color="#22c55e" />}
                  color="green"
                  growth={getStatValue(combinedReportsData.salesStats, 'incomeGrowth', null)}
                  isLoading={combinedReportsData.loading?.salesStats}
                />

                <QuickStatsCard
                  title="Subscriptions"
                  value={getStatValue(combinedReportsData.salesStats, 'totalSubscriptions', '0')}
                  icon={<FontAwesome5 name="credit-card" size={20} color="#f59e0b" />}
                  color="amber"
                  growth={getStatValue(combinedReportsData.salesStats, 'subscriptionGrowth', null)}
                  isLoading={combinedReportsData.loading?.salesStats}
                />

                <QuickStatsCard
                  title="CRM Contacts"
                  value={getStatValue(combinedReportsData, 'crmCount', '0')}
                  icon={<FontAwesome5 name="address-book" size={20} color="#8b5cf6" />}
                  color="grey"
                  isLoading={combinedReportsData.loading?.crmCount}
                />
              </View>
              </View>

              {/* Extended Reports */}
              {Array.isArray(extendedReports) && extendedReports.length > 0 && (
                <Animatable.View animation="slideInUp" duration={500}>
                  <SectionHeader
                    title="Extended Reports"
                    subtitle={`${extendedReports.length} additional reports available`}
                  />
                  <ScrollView
                    horizontal 
                    style={heroStyles.horizontalScroll}
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ paddingHorizontal: isMobile ? 16 : 20 }}
                  >
                    {extendedReports.map((report, index) => (
                      <Animatable.View
                        key={index}
                        animation="fadeInRight"
                        delay={index * 100}
                        duration={400}
                      >
                        <Pressable
                          style={[heroStyles.glassCard, heroStyles.reportCard, { marginRight: 16 }]}
                          onPress={() => _reportCallback(report)}
                        >
                          <LinearGradient
                            colors={theme
                              ? ['rgba(255, 255, 255, 0.95)', 'rgba(255, 255, 255, 0.85)']
                              : ['rgba(30, 30, 30, 0.95)', 'rgba(30, 30, 30, 0.85)']}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={heroStyles.gradientBackground}
                          />
                          <View style={heroStyles.contentContainer}>
                            <Text style={[heroStyles.reportTitle, { color: textColor }]}>
                              {String(parseByDelimiter(report?.title || `Report ${index + 1}`))}
                            </Text>
                            <Text style={[heroStyles.reportSubtitle, { color: mutedTextColor }]}>
                              Tap to view details
                            </Text>
                          </View>
                        </Pressable>
                      </Animatable.View>
                    ))}
                  </ScrollView>
                </Animatable.View>
              )}
            </>
          ) : currentView === 'comprehensive' ? (
              <ErrorBoundary>
                <ComprehensiveAnalytics dateRange={dateSelected} />
              </ErrorBoundary>
            ) : currentView === 'usage' ? (
                <ErrorBoundary>
                  <UsageInsights dateRange={dateSelected} />
                </ErrorBoundary>
              ) : currentView === 'crm' ? (
                  <ErrorBoundary>
                    <CRMAnalytics dateRange={dateSelected} />
                  </ErrorBoundary>
                ) : currentView === 'products' ? (
                    <ErrorBoundary>
                      <ProductAnalytics dateRange={dateSelected} />
                    </ErrorBoundary>
                  ) : currentView === 'analytics' ? (
                      <ErrorBoundary>
                        <AdvancedAnalyticsDashboard />
                      </ErrorBoundary>
                    ) : (
            <ErrorBoundary>
              <AnalyticsDemoData />
            </ErrorBoundary>
          )}
        </ScrollView>
      </DefaultScreen>
    </View>
  );
}

// Hero-inspired styling matching Hero.tsx
const heroStyles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  floatingBlur: {
    position: 'absolute',
    width: 256,
    height: 256,
    borderRadius: 128,
    backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(60px)',
    }),
    zIndex: 1,
  },
  glassCard: {
    borderRadius: 24,
    overflow: 'hidden',
    position: 'relative',
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: Platform.OS === 'web' ? 0.15 : 0.2,
    shadowRadius: 24,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    }),
  },
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  contentContainer: {
    position: 'relative',
    zIndex: 3,
    backgroundColor: Platform.OS === 'web'
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(255, 255, 255, 0.98)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
    }),
    padding: 20,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  growthText: {
    fontSize: 12,
    fontWeight: '600',
  },
  sectionHeader: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    fontWeight: '400',
  },
  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 20,
    marginHorizontal: 4,
    minWidth: 120,
  },
  viewButtonActive: {
    backgroundColor: '#3b82f6',
  },
  viewButtonInactive: {
    backgroundColor: 'transparent',
  },
  viewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statsGrid: {
    gap: 16,
  },
  horizontalScroll: {
    marginBottom: 16,
  },
  reportCard: {
    minWidth: 200,
    maxWidth: 300,
  },
  reportTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  reportSubtitle: {
    fontSize: 12,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
});

export default ReportsScreen;